/* dev-quick-shop.js */
(function () {
  'use strict';

  class DevQuickShop extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.dialog = this.querySelector('.quick-shop__dialog');
      this.form = this.querySelector('.quick-shop__form');

      this._onKeydown = this._onKeydown.bind(this);
      this.close = this.close.bind(this);

      var overlay = this.querySelector('[data-qs-overlay]');
      if (overlay) overlay.addEventListener('click', this.close);
      this.querySelectorAll('[data-qs-close]').forEach(function (btn) {
        btn.addEventListener('click', this.close);
      }, this);

      // Option switching — highlight + submit button price
      this.querySelectorAll('.quick-shop__radio-input').forEach(function (input) {
        input.addEventListener('change', this._onOptionChange.bind(this));
      }, this);

      if (this.form) {
        this.form.addEventListener('submit', this._onSubmit.bind(this));
      }
    }

    _onOptionChange(e) {
      var option = e.target.closest('[data-qs-option]');
      this.querySelectorAll('[data-qs-option]').forEach(function (el) {
        el.classList.toggle('quick-shop__option--selected', el === option);
      });
      var priceEl = this.querySelector('[data-qs-price]');
      if (priceEl && option && option.dataset.price) {
        priceEl.textContent = option.dataset.price;
      }
    }

    open() {
      if (this._isOpen) return;
      this._isOpen = true;
      this._lastFocused = document.activeElement;

      // The discount-link script may have swapped the checked option's price since render.
      var checked = this.querySelector('.quick-shop__radio-input:checked');
      if (checked) this._onOptionChange({ target: checked });

      this.removeAttribute('hidden');
      // Force a reflow so the transition runs from the hidden state.
      void this.offsetWidth;
      this.classList.add('is-open');

      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeydown);

      var target = this.querySelector('[data-qs-close]');
      if (target) {
        window.setTimeout(function () {
          target.focus({ preventScroll: true });
        }, 80);
      }
    }

    close() {
      if (!this._isOpen) {
        this.setAttribute('hidden', '');
        return;
      }
      this._isOpen = false;

      this.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKeydown);

      var self = this;
      window.setTimeout(function () {
        if (!self._isOpen) self.setAttribute('hidden', '');
      }, 380);

      if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
        this._lastFocused.focus();
      }
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
      } else if (e.key === 'Tab') {
        this._trapFocus(e);
      }
    }

    _trapFocus(e) {
      var focusable = this.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* =============================================
       Add to Cart (AJAX + open drawer) — mirrors
       the PDP flow in dev-product-information.js
       ============================================= */

    async _onSubmit(e) {
      e.preventDefault();

      var variantId = this.form.querySelector('input[name="id"]')?.value;
      if (!variantId) return;

      var addBtn = this.form.querySelector('[name="add"]');
      var btnText = addBtn ? addBtn.querySelector('.dev-btn__text') : null;
      var errorEl = this.querySelector('[data-qs-error]');
      if (errorEl) errorEl.setAttribute('hidden', '');

      if (addBtn) {
        addBtn.classList.add('dev-btn--disabled');
        if (btnText) btnText.textContent = 'Adding...';
      }

      try {
        var id = parseInt(variantId, 10);
        var checkedPlan = this.form.querySelector('input[name="selling_plan"]:checked');
        var sellingPlanId = checkedPlan && checkedPlan.value ? parseInt(checkedPlan.value, 10) : null;
        // Last choice wins, same as the PDP: the opposite purchase type's line moves over
        var moved = await this._takeOppositeLines(id, sellingPlanId);
        var item = { id: id, quantity: 1 + moved };
        if (sellingPlanId) item.selling_plan = sellingPlanId;

        var response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });

        if (!response.ok) {
          var err = await response.json();
          throw new Error(err.description || 'Could not add to cart');
        }
        this._takenLines = []; // added — nothing to put back from here on
        await response.json();

        this.close();
        await this._openCartDrawer();
        await this._updateCartBadge();
      } catch (error) {
        await this._restoreTakenLines();
        console.error('Quick shop add to cart failed:', error);
        if (errorEl) {
          errorEl.textContent = error.message || 'Could not add to cart. Please try again.';
          errorEl.removeAttribute('hidden');
        }
      } finally {
        if (addBtn) {
          addBtn.classList.remove('dev-btn--disabled');
          if (btnText) btnText.textContent = 'Add to Cart';
        }
      }
    }

    /**
     * Removes lines of this variant with the opposite purchase type (one-time vs subscription)
     * and returns their quantity. Same-type lines are left to Shopify, which merges them.
     * The removed lines are kept on this._takenLines so a failed add can put them back.
     * @param {number} variantId
     * @param {number | null} sellingPlanId
     * @returns {Promise<number>}
     */
    async _takeOppositeLines(variantId, sellingPlanId) {
      var moved = 0;
      this._takenLines = [];
      try {
        var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return 0;
        var cart = await res.json();
        var opposite = (cart.items || []).filter(function (line) {
          return line.variant_id === variantId && !!line.selling_plan_allocation !== !!sellingPlanId;
        });
        for (var i = 0; i < opposite.length; i++) {
          var change = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: opposite[i].key, quantity: 0 }),
          });
          if (change.ok) {
            moved += opposite[i].quantity;
            this._takenLines.push(opposite[i]);
          }
        }
      } catch (e) {
        /* cart unreadable — just add */
      }
      return moved;
    }

    /** Puts back the lines _takeOppositeLines removed, when the add that should replace them failed. */
    async _restoreTakenLines() {
      var items = (this._takenLines || []).map(function (line) {
        var item = { id: line.variant_id, quantity: line.quantity };
        var plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
        if (plan && plan.id) item.selling_plan = plan.id;
        if (line.properties && Object.keys(line.properties).length) item.properties = line.properties;
        return item;
      });
      this._takenLines = [];
      if (!items.length) return;
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        });
      } catch (e) {
        /* best effort */
      }
    }

    async _openCartDrawer() {
      var drawer = document.querySelector('cart-drawer-component');
      if (!drawer) return;

      // Refresh drawer content first, then open
      var sectionId = drawer.querySelector('cart-items-component')?.dataset?.sectionId;
      if (sectionId) {
        try {
          var sectionsRes = await fetch(`${window.location.pathname}?sections=${sectionId}`);
          var sections = await sectionsRes.json();
          var html = sections[sectionId];
          if (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var newInner = doc.querySelector('[data-hydration-key="cart-drawer-inner"]');
            var currentInner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');
            if (newInner && currentInner) {
              currentInner.innerHTML = newInner.innerHTML;
            }
          }
        } catch (err) {
          console.error('Drawer refresh failed:', err);
        }
      }

      drawer.open();
    }

    async _updateCartBadge() {
      try {
        var cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        var cart = await cartRes.json();
        document.querySelectorAll('.site-header__cart-count').forEach(function (el) {
          el.textContent = cart.item_count;
          el.setAttribute('aria-label', `${cart.item_count} items`);
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
        document.querySelectorAll('.cart-bubble__text').forEach(function (el) {
          el.textContent = cart.item_count > 99 ? '99+' : cart.item_count;
        });
        document.querySelectorAll('.cart-bubble').forEach(function (el) {
          el.classList.toggle('hidden', cart.item_count === 0);
        });
      } catch (e) {
        /* ignore badge update failure */
      }
    }
  }

  if (!window.customElements.get('dev-quick-shop')) {
    window.customElements.define('dev-quick-shop', DevQuickShop);
  }

  // Card CTA opens the popup inside the same card. Falls back to the product
  // page link when JS/custom elements are unavailable.
  if (!window.__devQuickShopBound) {
    window.__devQuickShopBound = true;
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-quick-shop-trigger]');
      if (!trigger) return;
      var card = trigger.closest('article');
      if (!card) return;
      var popup = card._devQuickShop || card.querySelector('dev-quick-shop');
      if (!popup || typeof popup.open !== 'function') return;
      e.preventDefault();
      // Teleport to <body>: a transformed ancestor (e.g. a slider track)
      // turns position:fixed into a local box and traps the overlay inside
      // the section. Keep a reference on the card for repeat opens.
      if (popup.parentElement !== document.body) {
        card._devQuickShop = popup;
        document.body.appendChild(popup);
      }
      popup.open();
    });
  }
})();
